import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import analyticsService from "../../services/analyticsService";
import "./EventAnalytics.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const EventAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    platform: "",
    source: "",
    eventType: "",
    page: 1,
    limit: 50,
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsService.getEventAnalytics(filters);
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.error || "Failed to fetch event analytics");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="event-analytics">
        <h1>Event Analytics</h1>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-analytics">
        <h1>Event Analytics</h1>
        <div className="error-container">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button onClick={fetchData} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  const { summary, statistics } = data;

  // Chart data
  const platformChartData = {
    labels: statistics.eventsByPlatform.map((p) => p._id || "Unknown"),
    datasets: [{
      data: statistics.eventsByPlatform.map((p) => p.count),
      backgroundColor: ["#2196f3", "#4caf50", "#ff9800"],
    }],
  };

  const sourceChartData = {
    labels: statistics.eventsBySource.map((s) => s._id || "Unknown"),
    datasets: [{
      data: statistics.eventsBySource.map((s) => s.count),
      backgroundColor: ["#9c27b0", "#f44336", "#00bcd4", "#ffeb3b"],
    }],
  };

  const dailyChartData = {
    labels: statistics.eventsByDay.map((d) => d._id),
    datasets: [{
      label: "Events",
      data: statistics.eventsByDay.map((d) => d.count),
      borderColor: "#2196f3",
      backgroundColor: "rgba(33, 150, 243, 0.1)",
      tension: 0.4,
    }],
  };

  return (
    <div className="event-analytics">
      <div className="analytics-header">
        <h1>Event Analytics</h1>
        <button onClick={fetchData} className="refresh-button">
          <span className="material-symbols-outlined">refresh</span>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select
          value={filters.platform}
          onChange={(e) => setFilters({ ...filters, platform: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Platforms</option>
          <option value="android">Android</option>
          <option value="ios">iOS</option>
          <option value="web">Web</option>
        </select>

        <select
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Sources</option>
          <option value="facebook">Facebook</option>
          <option value="google">Google</option>
          <option value="app">App</option>
          <option value="custom">Custom</option>
        </select>

        <select
          value={filters.eventType}
          onChange={(e) => setFilters({ ...filters, eventType: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Event Types</option>
          <option value="standard">Standard</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="analytics-grid">
        <div className="analytics-card">
          <div className="card-icon events">
            <span className="material-symbols-outlined">analytics</span>
          </div>
          <div className="card-content">
            <h3>Total Events</h3>
            <p className="metric-value">{summary.totalEvents.toLocaleString()}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon users">
            <span className="material-symbols-outlined">person</span>
          </div>
          <div className="card-content">
            <h3>Unique Users</h3>
            <p className="metric-value">{summary.uniqueUsers.toLocaleString()}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon devices">
            <span className="material-symbols-outlined">devices</span>
          </div>
          <div className="card-content">
            <h3>Unique Devices</h3>
            <p className="metric-value">{summary.uniqueDevices.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>Events by Platform</h3>
          <div className="chart-container pie-chart">
            <Pie data={platformChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>

        <div className="chart-card">
          <h3>Events by Source</h3>
          <div className="chart-container pie-chart">
            <Pie data={sourceChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>

        <div className="chart-card full-width">
          <h3>Daily Event Trend (Last 7 Days)</h3>
          <div className="chart-container">
            <Line data={dailyChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      {/* Top Events Table */}
      <div className="table-card">
        <h3>Top Events</h3>
        <table className="events-table">
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Count</th>
              <th>Last Occurred</th>
            </tr>
          </thead>
          <tbody>
            {statistics.eventsByName.map((event, index) => (
              <tr key={index}>
                <td>{event._id || "Unknown"}</td>
                <td>{event.count.toLocaleString()}</td>
                <td>{new Date(event.lastOccurred).toLocaleString("en-US", { timeZone: "Asia/Dhaka" })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventAnalytics;
